/**
 * UpmpMap — shared Google Maps wrapper used by every staff-side surface that
 * needs an interactive map (NationalMap, DeliveryRoutes).
 *
 * Why a thin wrapper:
 *   - Centralises the LoadScript loader so consumers don't accidentally mount
 *     two scripts on the same page (the `@react-google-maps/api` loader
 *     dedupes by `id`, but only when the props match).
 *   - Hides the API-key absence behind a styled fallback so demos don't blow
 *     up with Google's `InvalidKeyMapError` console banner.
 *   - Memoises so prop changes from a parent re-render don't cause the map
 *     div to re-mount and reset zoom/centre.
 *
 * Props are immutable (`readonly`) so React's reference equality plays nicely
 * with the memoisation. Markers and polylines opt in to a stable `id` to keep
 * keys deterministic.
 */
import { type JSX, memo } from 'react';
import { GoogleMap, LoadScript, Marker, Polyline } from '@react-google-maps/api';
import { MapPin } from 'lucide-react';

import { googleMapsApiKey } from '../../lib/env';

export interface UpmpMapMarker {
  readonly id: string;
  readonly position: { readonly lat: number; readonly lng: number };
  readonly title?: string;
  readonly label?: string;
  readonly onClick?: () => void;
}

export interface UpmpMapPolyline {
  readonly id: string;
  readonly path: readonly { readonly lat: number; readonly lng: number }[];
  readonly color?: string;
}

export interface UpmpMapProps {
  readonly center: { readonly lat: number; readonly lng: number };
  readonly zoom?: number;
  readonly markers?: readonly UpmpMapMarker[];
  readonly polylines?: readonly UpmpMapPolyline[];
  readonly height?: number | string;
  readonly className?: string;
}

const DEFAULT_ZOOM = 6; // Nigeria fits at zoom 6 from a lat/lng-9.0/lng-8.0 centre.
const DEFAULT_HEIGHT = 480;
const SCRIPT_ID = 'upmp-google-maps-script';

function MapDisabledNotice({ height }: { height: number | string }): JSX.Element {
  const style = { height: typeof height === 'number' ? `${height}px` : height };
  return (
    <div
      className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-col items-center justify-center text-amber-900"
      style={style}
      data-testid="upmp-map-disabled"
    >
      <MapPin className="w-8 h-8 text-amber-700 mb-2" />
      <p className="font-semibold text-sm">Map disabled</p>
      <p className="mt-1 text-xs text-amber-800/90 max-w-md text-center">
        Set <code className="font-mono">VITE_GOOGLE_MAPS_API_KEY</code> in{' '}
        <code className="font-mono">.env</code> to enable the live map. The SDK is loaded
        lazily — no requests fire when the key is empty.
      </p>
    </div>
  );
}

function UpmpMapInner({
  center,
  zoom = DEFAULT_ZOOM,
  markers,
  polylines,
  height = DEFAULT_HEIGHT,
  className,
}: UpmpMapProps): JSX.Element {
  const apiKey = googleMapsApiKey();

  if (apiKey === '') {
    return <MapDisabledNotice height={height} />;
  }

  const containerStyle: React.CSSProperties = {
    width: '100%',
    height: typeof height === 'number' ? `${height}px` : height,
  };

  return (
    <div className={className} data-testid="upmp-map">
      <LoadScript googleMapsApiKey={apiKey} id={SCRIPT_ID}>
        <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={zoom}>
          {(markers ?? []).map((m) => (
            <Marker
              key={m.id}
              position={{ lat: m.position.lat, lng: m.position.lng }}
              {...(m.title !== undefined && { title: m.title })}
              {...(m.label !== undefined && { label: m.label })}
              {...(m.onClick !== undefined && { onClick: m.onClick })}
            />
          ))}
          {(polylines ?? []).map((p) => (
            <Polyline
              key={p.id}
              path={p.path.map((pt) => ({ lat: pt.lat, lng: pt.lng }))}
              options={{
                strokeColor: p.color ?? '#27245f',
                strokeOpacity: 0.9,
                strokeWeight: 3,
              }}
            />
          ))}
        </GoogleMap>
      </LoadScript>
    </div>
  );
}

export const UpmpMap = memo(UpmpMapInner);
